import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Building2, Users, FileText, UserCheck, TrendingUp, Activity } from 'lucide-react';
import KPICard from '../components/dashboard/KPICard';
import { AttendanceChart, EKYCChart } from '../components/dashboard/Charts';
import { DashboardMetrics, AttendanceData } from '../data/mockData';
import { dashboardService } from '../services/dashboardService';

const Dashboard: React.FC = () => {
    const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
    const [attendanceData, setAttendanceData] = useState<AttendanceData[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
                const [metricsData, chartData] = await Promise.all([dashboardService.getMetrics(), dashboardService.getAttendanceChart()]);

                setMetrics(metricsData);
                setAttendanceData(chartData);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    if (loading || !metrics) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading dashboard data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col space-y-2">
                <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
                <p className="text-gray-600">Real-time insights into Face Authentication implementation across Drishti programs</p>
            </motion.div>

            {/* KPI Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <KPICard title="States/UTs with Face Auth" value={metrics.totalStates} subtitle="Total administrative states" icon={MapPin} color="blue" trend={{ value: 12.5, isPositive: true }} delay={0.1} />

                <KPICard title="Districts Covered" value={metrics.totalDistricts} subtitle="Out of 756 total districts" icon={Building2} color="green" trend={{ value: 8.2, isPositive: true }} delay={0.15} />

                <KPICard title="Blocks Implemented" value={metrics.totalBlocks} subtitle="Across all states" icon={Activity} color="purple" trend={{ value: 15.7, isPositive: true }} delay={0.2} />

                <KPICard title="Villages with Face Auth" value={metrics.totalVillages} subtitle="Rural and urban areas" icon={MapPin} color="orange" trend={{ value: 22.3, isPositive: true }} delay={0.25} />
            </div>

            {/* Second Row KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <KPICard title="Musterrolls with Face Auth" value={metrics.totalMusterrolls} subtitle="Digital attendance records" icon={FileText} color="blue" trend={{ value: 18.9, isPositive: true }} delay={0.3} />

                <KPICard title="Face Auth Attendance" value={metrics.totalPersonsWithFaceAuth} subtitle="Total person-days recorded" icon={Users} color="green" trend={{ value: 25.4, isPositive: true }} delay={0.35} />

                <KPICard
                    title="eKYC Completion Rate"
                    value={`${((metrics.ekycDone / (metrics.ekycDone + metrics.ekycNotDone)) * 100).toFixed(1)}%`}
                    subtitle={`${metrics.ekycDone.toLocaleString()} of ${(metrics.ekycDone + metrics.ekycNotDone).toLocaleString()} beneficiaries`}
                    icon={UserCheck}
                    color="purple"
                    trend={{ value: 5.2, isPositive: true }}
                    delay={0.4}
                />
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AttendanceChart data={attendanceData} />
                <EKYCChart ekycDone={metrics.ekycDone} ekycNotDone={metrics.ekycNotDone} />
            </div>

            {/* Quick Stats Section */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                <div className="flex items-center space-x-3 mb-4">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-900">Implementation Highlights</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                    <div className="space-y-2">
                        <div className="text-2xl font-bold text-blue-600">{((metrics.totalPersonsWithFaceAuth / (metrics.ekycDone + metrics.ekycNotDone)) * 100).toFixed(1)}%</div>
                        <div className="text-sm text-gray-600">Face Auth Adoption Rate</div>
                    </div>

                    <div className="space-y-2">
                        <div className="text-2xl font-bold text-green-600">{(metrics.totalMusterrolls / metrics.totalBlocks).toFixed(0)}</div>
                        <div className="text-sm text-gray-600">Avg. Musterrolls per Block</div>
                    </div>

                    <div className="space-y-2">
                        <div className="text-2xl font-bold text-purple-600">{(metrics.totalPersonsWithFaceAuth / metrics.totalMusterrolls).toFixed(0)}</div>
                        <div className="text-sm text-gray-600">Avg. Workers per Musterroll</div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default Dashboard;
